import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import authService from "./authService";
import { getUserProfile } from "../profile/profileService";
import { fetchAuthSession } from "aws-amplify/auth";

export const AuthContext = createContext();

// A profile can exist in the DB with a stale/blank email (e.g. it predates
// an email being added in Cognito, or was manually cleared) even though the
// user's actual account has one. getUserProfile now flags this
// unconditionally via profile.emailMissing — independent of whether
// notifications happen to be enabled, since an incomplete profile is the
// underlying problem either way.
const deriveEmailMissing = (profile) => !!profile?.emailMissing;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [hasProfile, setHasProfile] = useState(false);
  const [emailMissing, setEmailMissing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const tokenCheckTimer = useRef(null);

  // [F01/F11] Refs so the interval callback always reads current values
  // rather than closing over stale state from when the interval was created.
  // This prevents the 2-minute countdown resetting on every auth state change,
  // and eliminates the logout race condition.
  const isAuthenticatedRef = useRef(isAuthenticated);
  const isLoggingOutRef = useRef(isLoggingOut);

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    isLoggingOutRef.current = isLoggingOut;
  }, [isLoggingOut]);

  const areTokensExpired = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      if (!session.tokens?.accessToken || !session.tokens?.idToken) {
        return true;
      }

      const now = Math.floor(Date.now() / 1000);
      const accessTokenExp = session.tokens.accessToken.payload.exp;
      const idTokenExp = session.tokens.idToken.payload.exp;

      const buffer = 30;
      return accessTokenExp <= now + buffer || idTokenExp <= now + buffer;
    } catch (error) {
      console.warn("Error checking token expiration:", error);
      return true;
    }
  }, []);

  const stopTokenMonitoring = useCallback(() => {
    if (tokenCheckTimer.current) {
      clearInterval(tokenCheckTimer.current);
      tokenCheckTimer.current = null;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      setIsLoggingOut(true);
      stopTokenMonitoring();
      setUser(null);
      setIsAuthenticated(false);
      setHasProfile(false);
      setEmailMissing(false);
      await authService.logout();
    } catch (error) {
      console.warn("Logout service error:", error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setHasProfile(false);
      setEmailMissing(false);
      setIsLoggingOut(false);
    }
  }, [stopTokenMonitoring]);

  const handleTokenExpiration = useCallback(async () => {
    if (tokenCheckTimer.current) {
      clearInterval(tokenCheckTimer.current);
      tokenCheckTimer.current = null;
    }
    await logout();
  }, [logout]);

  // [F01/F11] startTokenMonitoring no longer depends on isAuthenticated or
  // isLoggingOut — it reads them from refs instead. This means the interval
  // is created once and stays stable; it won't be recreated on every auth
  // state change, so the 2-minute countdown is never accidentally reset.
  const startTokenMonitoring = useCallback(() => {
    if (tokenCheckTimer.current) {
      clearInterval(tokenCheckTimer.current);
    }

    tokenCheckTimer.current = setInterval(
      async () => {
        // Read current values from refs — never stale
        if (isAuthenticatedRef.current && !isLoggingOutRef.current) {
          const expired = await areTokensExpired();
          if (expired) {
            await handleTokenExpiration();
          }
        }
      },
      2 * 60 * 1000,
    );
  }, [areTokensExpired, handleTokenExpiration]);
  // isAuthenticated and isLoggingOut intentionally removed from deps —
  // refs are used inside the callback instead.

  const checkUser = useCallback(async () => {
    try {
      setLoading(true);

      if (isLoggingOut) return;

      const currentUser = await authService.getCurrentUser();
      if (currentUser) {
        const expired = await areTokensExpired();
        if (expired) {
          setUser(null);
          setIsAuthenticated(false);
          setHasProfile(false);
          setEmailMissing(false);
          stopTokenMonitoring();
          return;
        }

        setUser(currentUser);
        setIsAuthenticated(true);
        startTokenMonitoring();

        if (currentUser.isConfirmed) {
          try {
            const profile = await getUserProfile(currentUser.sub);
            setHasProfile(profile && profile.favouriteTeamId ? true : false);
            setEmailMissing(deriveEmailMissing(profile));
          } catch (profileError) {
            if (profileError.message?.includes("not authenticated")) {
              setUser(null);
              setIsAuthenticated(false);
              setHasProfile(false);
              setEmailMissing(false);
              stopTokenMonitoring();
              return;
            }
            setHasProfile(false);
            setEmailMissing(false);
          }
        } else {
          setHasProfile(false);
          setEmailMissing(false);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setHasProfile(false);
        setEmailMissing(false);
        stopTokenMonitoring();
      }
    } catch (err) {
      console.error("Error checking authenticated user:", err);
      setUser(null);
      setIsAuthenticated(false);
      setHasProfile(false);
      setEmailMissing(false);
      stopTokenMonitoring();
    } finally {
      setLoading(false);
    }
  }, [
    isLoggingOut,
    areTokensExpired,
    startTokenMonitoring,
    stopTokenMonitoring,
  ]);

  useEffect(() => {
    checkUser();
    return () => {
      stopTokenMonitoring();
    };
  }, [checkUser, stopTokenMonitoring]);

  // Check tokens when page becomes visible (user returns to tab)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (
        !document.hidden &&
        isAuthenticatedRef.current &&
        !isLoggingOutRef.current
      ) {
        const expired = await areTokensExpired();
        if (expired) {
          await handleTokenExpiration();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [areTokensExpired, handleTokenExpiration]);
  // isAuthenticated and isLoggingOut removed from deps here too —
  // refs are read inside the handler instead.

  const login = async (username, password) => {
    await authService.login(username, password);
    await checkUser();
  };

  const refreshProfile = async () => {
    if (!user || !isAuthenticated || !user.isConfirmed || isLoggingOut) return;

    try {
      const profile = await getUserProfile(user.sub);
      setHasProfile(profile && profile.favouriteTeamId ? true : false);
      setEmailMissing(deriveEmailMissing(profile));
    } catch (profileError) {
      if (profileError.message?.includes("not authenticated")) {
        await handleTokenExpiration();
        return;
      }
      setHasProfile(false);
      setEmailMissing(false);
    }
  };

  const isConfirmed = user?.isConfirmed ?? false;

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        isAuthenticated,
        isConfirmed,
        isAdmin: user?.isAdmin ?? false,
        hasProfile,
        emailMissing,
        login,
        logout,
        loading,
        refreshProfile,
        isLoggingOut,
        checkTokenExpiration: areTokensExpired,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
